import { useState, useEffect, useCallback } from 'react';

function collectScrollRoots(start: HTMLElement | null): Set<Element> {
  const roots = new Set<Element>();
  let node: HTMLElement | null = start;
  while (node) {
    const { overflowY, overflowX } = window.getComputedStyle(node);
    if (
      /(auto|scroll|overlay)/.test(overflowY) ||
      /(auto|scroll|overlay)/.test(overflowX)
    ) {
      roots.add(node);
    }
    node = node.parentElement;
  }
  roots.add(document.documentElement);
  return roots;
}

interface SelectionData {
  text: string;
  x: number;
  y: number;
  isVisible: boolean;
}

export const useTextSelection = (containerRef?: React.RefObject<HTMLElement | null>) => {
  const [selection, setSelection] = useState<SelectionData>({
    text: '',
    x: 0,
    y: 0,
    isVisible: false
  });
  const handleSelection = useCallback(() => {
    const selectedText = window.getSelection()?.toString().trim();
    
    if (selectedText && selectedText.length > 0) {
      const selection = window.getSelection();
      const range = selection?.getRangeAt(0);
      
      // Check if selection is within the active message container.
      if (containerRef?.current && range) {
        const isWithinContainer = containerRef.current.contains(range.commonAncestorContainer);
        if (!isWithinContainer) {
          setSelection(prev => ({ ...prev, isVisible: false }));
          return;
        }
      }
      
      const rect = range?.getBoundingClientRect();
      
      if (rect) {
        setSelection({
          text: selectedText,
          x: rect.left + (rect.width / 2),
          y: rect.top + window.scrollY,
          isVisible: true
        });
      }
    } else {
      setSelection(prev => ({ ...prev, isVisible: false }));
    }
  }, [containerRef]);

  const clearSelection = useCallback(() => {
    setSelection(prev => ({ ...prev, isVisible: false }));
    window.getSelection()?.removeAllRanges();
  }, []);

  const hideMarkerOnly = useCallback(() => {
    setSelection(prev => (prev.isVisible ? { ...prev, isVisible: false } : prev));
  }, []);

  // Marker uses fixed coords from the selection rect; any scroll misaligns it — hide (keep text selection).
  useEffect(() => {
    if (!selection.isVisible || !containerRef?.current) return;

    const el = containerRef.current;
    const scrollRoots = collectScrollRoots(el);
    const scrollOpts: AddEventListenerOptions = { passive: true };
    const wheelOpts: AddEventListenerOptions = { capture: true, passive: true };

    scrollRoots.forEach((root) => {
      root.addEventListener('scroll', hideMarkerOnly, scrollOpts);
    });
    window.addEventListener('scroll', hideMarkerOnly, scrollOpts);
    el.addEventListener('wheel', hideMarkerOnly, wheelOpts);

    return () => {
      scrollRoots.forEach((root) => {
        root.removeEventListener('scroll', hideMarkerOnly, scrollOpts);
      });
      window.removeEventListener('scroll', hideMarkerOnly, scrollOpts);
      el.removeEventListener('wheel', hideMarkerOnly, wheelOpts);
    };
  }, [selection.isVisible, containerRef, hideMarkerOnly]);

  useEffect(() => {
    const handleMouseUp = (e: MouseEvent) => {
      // Small delay to ensure selection is complete
      setTimeout(handleSelection, 10);
    };

    const handleMouseDown = (e: MouseEvent) => {
      // Don't hide if clicking on the quote marker
      const target = e.target as HTMLElement;
      if (target.closest('.quote-marker')) {
        return;
      }
      setSelection(prev => ({ ...prev, isVisible: false }));
    };

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        clearSelection();
      }
    };

    document.addEventListener('mouseup', handleMouseUp);
    document.addEventListener('mousedown', handleMouseDown);
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('mouseup', handleMouseUp);
      document.removeEventListener('mousedown', handleMouseDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleSelection, clearSelection, containerRef]);

  return {
    selection,
    clearSelection
  };
};
